import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import ConfirmDialog from '../../../common/components/confirm-dialog';
import { IconEdit, IconPlus, IconTrash } from '../../../common/icons';
import { recipientDetailAvailable } from '../../../api/provisional/availability';
import { LOW_COHORT_N, RECIPIENT_RATE_ROWS } from '../../../core/constants/kpis';
import { envelopeData } from '../../../core/network/envelope';
import { fmtNum, fmtPct } from '../../../core/utils/format';
import { useRecipientsList } from '../contexts/recipients';
import { useClientRecipientsKpi } from '../contexts/recipients-kpi';
import { useDeleteRecipient } from '../contexts/recipient-mutations';
import { recipientKeys } from '../queries/keys';
import RecipientForm from '../forms/recipient-form';
import { statusLabel, statusModifier } from '../utils/status';

import '../styles/overview.css';

// TSS vite/03 — parcerías overview. Lista de receptoras del cliente + escritura
// (Q-121). GET public/recipients = lectura COMPUESTA → envelope; items/pagination
// con envelopeData(). Las escrituras (form/borrado) leen r.data plano y refrescan
// por invalidación de recipientKeys (contexts/recipient-mutations.jsx).
//
// Las tarjetas enlazan al dashboard de receptora solo mientras esa superficie
// está disponible (PROVISIONAL(Q-118)); las acciones editar/borrar quedan fuera
// del enlace para no anidar interactivos. Paginación offset (contract: limit
// default 50 / máx 500).

const PAGE_SIZE = 50;

// Conteos de producción agregados que recipients_kpi PUEDE traer (lectura
// tolerante — se muestran solo los presentes; el backend fija el shape exacto).
const PROD_ROWS = [
    { key: 'transfers',   label: 'Transferencias' },
    { key: 'pregnancies', label: 'Preñeces' },
    { key: 'births',      label: 'Partos' },
];

// KPIs GENERALES de la parcería ("todo en general"): total de receptoras + tasas
// de cohorte (preñez, supervivencia) + conteos de producción si vienen. Fuente:
// public/stats?view=kpi → data.recipients_kpi (gated por available_modules —
// aparece solo cuando el rol partner tiene datos). Loading/error silenciosos: es
// un bloque opcional que enriquece la lista, no la bloquea.
function ParceriasKpiBlock({ total }) {
    const { data: envelope } = useClientRecipientsKpi();

    const kpi = envelope?.data?.recipients_kpi;
    const modules = envelope?.available_modules ?? [];
    if (!kpi || !modules.includes('recipients_kpi')) return null;

    const rates = kpi.rates || {};
    const rateRows = RECIPIENT_RATE_ROWS.filter((row) => rates[row.key]);
    const prod = kpi.production || kpi;
    const prodRows = PROD_ROWS.filter((row) => prod?.[row.key] != null);

    return (
        <div className="parcerias__kpis">
            <div className="parcerias-kpi parcerias-kpi--count">
                <span className="parcerias-kpi__value">{fmtNum(total ?? kpi.count)}</span>
                <span className="parcerias-kpi__label">Receptoras</span>
            </div>

            {rateRows.map((row) => {
                const rate = rates[row.key];
                const lowCohort = rate.cohort_n != null && rate.cohort_n > 0 && rate.cohort_n < LOW_COHORT_N;
                return (
                    <div key={row.key} className={`parcerias-kpi parcerias-kpi--rate parcerias-kpi--${rate.qualifier || 'none'}`}>
                        <span className="parcerias-kpi__value">{fmtPct(rate.value)}</span>
                        <span className="parcerias-kpi__label">{row.label}</span>
                        {lowCohort && (
                            <span className="parcerias-kpi__sub">
                                {fmtNum(rate.numerator)}/{fmtNum(rate.denominator)} · cohorte baja
                            </span>
                        )}
                    </div>
                );
            })}

            {prodRows.map((row) => (
                <div key={row.key} className="parcerias-kpi">
                    <span className="parcerias-kpi__value">{fmtNum(prod[row.key])}</span>
                    <span className="parcerias-kpi__label">{row.label}</span>
                </div>
            ))}
        </div>
    );
}

function RecipientCard({ recipient, onEdit, onDelete }) {
    const main = (
        <div className="parcerias-card__main">
            <span className="parcerias-card__name">{recipient.name || `Receptora ${recipient.number}`}</span>
            <div className="parcerias-card__meta">
                <span className="parcerias-card__number">Nº {recipient.number}</span>
                {recipient.farm?.name && (
                    <span className="parcerias-card__farm">{recipient.farm.name}</span>
                )}
            </div>
        </div>
    );

    return (
        <article className="parcerias-card">
            {recipientDetailAvailable ? (
                <Link to={`/parcerias/${recipient.id}`} className="parcerias-card__link">
                    {main}
                </Link>
            ) : (
                main
            )}
            <div className="parcerias-card__side">
                <span className={`parcerias-card__status ${statusModifier(recipient.status)}`}>
                    {statusLabel(recipient.status)}
                </span>
                <div className="parcerias-card__actions">
                    <button
                        type="button"
                        className="parcerias-card__action"
                        onClick={() => onEdit(recipient)}
                        aria-label={`Editar receptora ${recipient.number}`}
                    >
                        <IconEdit />
                    </button>
                    <button
                        type="button"
                        className="parcerias-card__action parcerias-card__action--danger"
                        onClick={() => onDelete(recipient)}
                        aria-label={`Eliminar receptora ${recipient.number}`}
                    >
                        <IconTrash />
                    </button>
                </div>
            </div>
        </article>
    );
}

export default function ParceriasOverview() {
    const [params, setParams] = useSearchParams();
    const qc = useQueryClient();

    const offset = Math.max(0, Number(params.get('offset')) || 0);

    const { data, isLoading, isFetching, isError, error } =
        useRecipientsList({ limit: PAGE_SIZE, offset });

    const payload = envelopeData(data);
    const items = payload.items ?? [];
    const pagination = payload.pagination ?? {};
    const total = pagination.total ?? pagination.count ?? null;

    const hasPrev = offset > 0;
    const hasNext = pagination.hasNext
        ?? (total != null ? offset + items.length < total : items.length === PAGE_SIZE);

    // UI state (modales) — local.
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const del = useDeleteRecipient();

    const goTo = (nextOffset) => {
        const next = new URLSearchParams(params);
        if (nextOffset > 0) next.set('offset', String(nextOffset));
        else next.delete('offset');
        setParams(next, { replace: true });
    };

    const openCreate = () => { setEditing(null); setFormOpen(true); };
    const openEdit = (recipient) => { setEditing(recipient); setFormOpen(true); };
    const closeForm = () => { setFormOpen(false); setEditing(null); };

    const confirmDelete = () => {
        if (!deleting) return;
        del.mutate(deleting.id, {
            onSuccess: () => {
                toast.success('Receptora eliminada');
                setDeleting(null);
            },
            onError: (err) => {
                if (err?.status === 404) {
                    toast.error('Receptora no encontrada');
                    qc.invalidateQueries({ queryKey: recipientKeys.all });
                    setDeleting(null);
                } else if (err?.status !== 403) {
                    toast.error(err?.message || 'No se pudo eliminar la receptora');
                }
            },
        });
    };

    if (isLoading) {
        return <div className="parcerias__loading">Cargando parcerías...</div>;
    }

    if (isError) {
        return (
            <div className="parcerias__error">
                {error?.message || 'No se pudieron cargar tus parcerías'}
            </div>
        );
    }

    return (
        <section className="parcerias">
            <header className="parcerias__header">
                <div className="parcerias__heading">
                    <h2 className="parcerias__title">Mis parcerías</h2>
                    <span className="parcerias__count">
                        {total != null ? total : items.length} receptora{(total ?? items.length) === 1 ? '' : 's'}
                    </span>
                </div>
                <button type="button" className="parcerias__new" onClick={openCreate}>
                    <IconPlus />
                    <span>Nueva receptora</span>
                </button>
            </header>

            <ParceriasKpiBlock total={total} />

            {!items.length ? (
                <p className="parcerias__empty">
                    Aún no tienes receptoras registradas en tus parcerías.
                </p>
            ) : (
                <div className={`parcerias__list ${isFetching ? 'parcerias__list--refreshing' : ''}`}>
                    {items.map((recipient) => (
                        <RecipientCard
                            key={recipient.id}
                            recipient={recipient}
                            onEdit={openEdit}
                            onDelete={setDeleting}
                        />
                    ))}
                </div>
            )}

            {(hasPrev || hasNext) && (
                <nav className="parcerias__pager">
                    <button
                        className="parcerias__pager-btn"
                        onClick={() => goTo(offset - PAGE_SIZE)}
                        disabled={!hasPrev || isFetching}
                    >
                        ← Anteriores
                    </button>
                    <span className="parcerias__pager-info">
                        {offset + 1}–{offset + items.length}
                    </span>
                    <button
                        className="parcerias__pager-btn"
                        onClick={() => goTo(offset + PAGE_SIZE)}
                        disabled={!hasNext || isFetching}
                    >
                        Siguientes →
                    </button>
                </nav>
            )}

            {formOpen && (
                <RecipientForm recipient={editing} onClose={closeForm} onSaved={closeForm} />
            )}

            <ConfirmDialog
                open={!!deleting}
                title="Eliminar receptora"
                message={
                    deleting
                        ? `¿Seguro que quieres eliminar la receptora Nº ${deleting.number}? Esta acción no se puede deshacer.`
                        : ''
                }
                confirmLabel="Eliminar"
                danger
                busy={del.isPending}
                onConfirm={confirmDelete}
                onClose={() => setDeleting(null)}
            />
        </section>
    );
}
